import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import type { Boot } from 'portlet/config/boot';
import type { HttpResponse, HttpService } from 'portlet/common/services';
import type { EmailTemplate, PortletDebugInformation, PortletDebugInformationItem } from 'header/types';

export type Message = {
    message: {
        subject: string;
        body: {
            contentType: 'HTML';
            content: string;
        };
        toRecipients: Array<{
            emailAddress: {
                address: string;
            };
        }>;
    };
};

export class MailingService {
    constructor(private readonly http: HttpService, private readonly boot: Boot) {}
    private graphApiServer = `${this.boot.servers.graphApiServer.url}/v1.0/me/sendMail`;
    private contentAreaUrl = this.boot.servers.contentArea.url;

    public sendMail(
        email: EmailTemplate,
        debugInfo: PortletDebugInformation,
    ): Observable<HttpResponse> {
        const body = MailingService.convertToMessage(
            email,
            MailingService.convertDebugDataToHTML(debugInfo),
        );

        return this.http.post<HttpResponse>(this.graphApiServer, {
            credentials: 'include',
            body,
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    public getContentAreaMail(contentAreaId: string): Observable<string> {
        return this.http
            .get<string>(`${this.contentAreaUrl}${contentAreaId}`, {
                credentials: 'include',
            })
            .pipe(map(({ data }) => data));
    }

    public static convertDebugDataToHTML(debugInfo: PortletDebugInformation): string {
        function getDebugItemsHTML(item: PortletDebugInformationItem): string {
            const tableHeader = (value: string) => `<h3>${value}</h3>`;
            const tableData = (value: Record<string, unknown> | null) =>
                !value || !Object.entries(value).length
                    ? `None`
                    : Object.entries(value)
                          .map(
                              ([key, value]) =>
                                  `<tr><td>${key}</td>${
                                      typeof value === 'string'
                                          ? `<td>${value}</td>`
                                          : `<td><pre>${JSON.stringify(value, null, 2)}</pre></td>`
                                  }</tr>`,
                          )
                          .join('\n');

            return `${tableHeader(item.label)}<table class="debug-data">${tableData(
                item.value,
            )}</table>`;
        }

        return Object.values(debugInfo)
            .map(value => `${getDebugItemsHTML(value)}`)
            .join('\n');
    }

    private static convertToMessage(email: EmailTemplate, debugInfoHTML: string): Message {
        const mailContent = `
                            <html>
                                <head>
                                    <style>
                                    body {
                                        font-family: sans-serif;
                                    }
                                    table {
                                        border-collapse: collapse;
                                    }
                                    td {
                                        padding: 0.5rem;
                                        border: 1px solid #808080;
                                    }
                                    </style>
                                <body>
                                    <h2>Description</h2>
                                    <p>
                                        ${email.description}
                                    </p>
                                    <hr />
                                    <h2>Debug information</h2>

                                    ${debugInfoHTML}
                                </body>
                            </html>
                            `;

        return {
            message: {
                subject: email.subject,
                body: {
                    contentType: 'HTML',
                    content: mailContent,
                },
                toRecipients: [
                    {
                        emailAddress: {
                            address: email.recipient,
                        },
                    },
                ],
            },
        };
    }
}
